// @internal
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { OutlineStyles, StyledSideNavProps } from '../../styles';
import { sidenavTokens } from '../../../../tokens/components/SideNav/tokens';
import { SideNavGroupItemProps } from './SideNavGroupItem';

type StyledSideNavGroupItemProps = Pick<SideNavGroupItemProps, 'isActive' | 'isVisible'> &
  Pick<StyledSideNavProps, 'isClosed'>;

const SideNavGroupItemStyles = css<StyledSideNavGroupItemProps>`
  align-items: center;
  border-left: ${sidenavTokens.ItemBorderWidthBaseDefault} ${sidenavTokens.ItemBorderStyleBaseDefault}
    ${sidenavTokens.ItemBorderColorBaseDefault};
  color: ${sidenavTokens.FontColorBaseDefault};
  font-weight: ${sidenavTokens.GroupItemFontWeightBaseDefault};
  justify-content: space-between;
  padding: ${sidenavTokens.GroupItemPaddingBaseVertical} ${sidenavTokens.GroupItemPaddingBaseHorizontal};
  margin-left: ${sidenavTokens.GroupItemMarginLeftBaseDefault};
  transition: ${`background-color ${sidenavTokens.TransitionDurationBaseDefault} ${sidenavTokens.TransitionCurveBaseDefault}, opacity 1s ${sidenavTokens.TransitionCurveBaseDefault}`};

  &:hover {
    background-color: ${sidenavTokens.ItemBackgroundColorActiveHover};
    text-decoration: none;
  }

  ${({ isClosed, isVisible }) =>
    !isClosed && isVisible
      ? css`
          display: flex;
          width: auto;
          opacity: 1;
        `
      : css`
          display: none;
          width: 0;
          opacity: 0;
        `}

  ${({ isActive }) =>
    isActive
      ? css`
          border-left: 0;
          padding-left: 21px;
          background-color: ${sidenavTokens.ItemBackgroundColorActiveDefault};
          box-shadow: ${sidenavTokens.ItemBorderWidthBaseDown} 0 0 0 ${sidenavTokens.ItemBorderColorBaseDown} inset;
          font-weight: ${sidenavTokens.GroupItemFontWeightBaseActive};
        `
      : css`
          background-color: transparent;
        `};

  ${OutlineStyles}

  &:focus-visible {
    border-radius: ${sidenavTokens.GroupItemBorderRadiusVisibleFocus};
    margin-right: ${sidenavTokens.GroupItemMarginRightVisibleFocus};
    padding-right: ${sidenavTokens.GroupItemPaddingRightVisibleFocus};
  }
`;

export const StyledSideNavGroupItemAnchor = styled('a').withConfig<StyledSideNavGroupItemProps>({
  shouldForwardProp: (prop, dv) => !['isActive', 'isVisible', 'isClosed'].includes(prop) || dv(prop),
})`
  ${SideNavGroupItemStyles}
`;

export const StyledSideNavGroupItemLink = styled(Link).withConfig<StyledSideNavGroupItemProps>({
  shouldForwardProp: (prop, dv) => !['isActive', 'isVisible', 'isClosed'].includes(prop) || dv(prop),
})`
  ${SideNavGroupItemStyles}
`;

type StyledSideNavGroupItemLabelProps = Pick<StyledSideNavProps, 'isClosed'> & React.HTMLAttributes<HTMLSpanElement>;

export const StyledSideNavGroupItemLabel = styled.span<StyledSideNavGroupItemLabelProps>`
  flex-grow: 1;
  flex-shrink: 0;
  line-height: 20px;
  max-width: ${sidenavTokens.GroupItemLabelMaxWidthBaseDefault};

  ${({ isClosed }) =>
    isClosed &&
    css`
      display: none;
      width: 0;
      max-width: 0;
    `}
`;
